import React, { useCallback, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { useNavigate } from '@lib/react-router-dom'

import { useAccountReadContext, useAccountWriteContext } from '@context/AccountContextContext'
import { useProductReadContext } from '@context/ProductContext'
import { useSessionLanguage } from '@context/SessionSettingsContext'

import { Button } from '@ui/components/Button'

import { isKYCUpdateRequired, isSumsubSupported } from '@utils/AccountAccess/accountKYCStatuses'
import { hasProductActivatedStatus } from '@utils/AccountAccess/accountProductStatuses'
import { CommonPaths } from '@utils/path'

import { AccountDetailedDto, isAccountDormantUpdateNeeded } from '@model/AccountDetailedDto'
import { isAccountActivatedStatus } from '@model/AccountDetailedStatus'

import { CompletionBlock } from '@global/completionBlock/CompletionBlock'
import { FormTemplate } from '@global/templates/FormTemplate'

import { useLandingPage } from '@hooks/useLandingPage'
import { useScrollToTop } from '@hooks/useScrollToTop'

import { Loading } from '@shared/components/Loading/Loading'
import {
  StatusTemplateBody,
  StatusTemplateButtons,
  StatusTemplateWrapper,
} from '@shared/templates/StatusTemplate/StatusTemplate'

import styles from './YearlyKycUpdateDetailsForm/YearlyKycUpdateDetailsForm.module.scss'

export const YearlyKycUpdateDetailsSuccessPage: React.FC = () => {
  useScrollToTop()
  const { t } = useTranslation()

  const navigate = useNavigate()
  const { refreshAccount } = useAccountWriteContext()
  const { account } = useAccountReadContext()
  const { product } = useProductReadContext()
  const locale = useSessionLanguage()
  const [hasRequestedRefresh, setHasRequestedRefresh] = useState(false)
  const [isLoading, setIsLoading] = useState(true)
  const { getLandingPage } = useLandingPage()

  const handleAccountReload = useCallback(async () => {
    if (hasRequestedRefresh) return

    setHasRequestedRefresh(true)
    setIsLoading(true)
    try {
      const newAccount = await refreshAccount(locale)

      if (shouldRedirectToSumsub(newAccount)) {
        navigate(CommonPaths.Sumsub)
      }
    } finally {
      setIsLoading(false)
    }
  }, [hasRequestedRefresh, locale, navigate, refreshAccount])

  useEffect(() => {
    handleAccountReload()
  }, [handleAccountReload])

  const isProductActivated = hasProductActivatedStatus(account, product)
  const goToDashboard = async () => {
    navigate(getLandingPage())
    await refreshAccount(locale)
  }

  return (
    <Loading isLoading={isLoading} showLoadingIcon>
      {isKYCUpdateRequired(account) &&
      !isProductActivated &&
      !isAccountDormantUpdateNeeded(account) ? (
        <FormTemplate
          title={t('Sign up.Registration Futures Trading')}
          contentClassName={styles.successContent}
        >
          <CompletionBlock onButtonClick={goToDashboard} />
        </FormTemplate>
      ) : (
        <StatusTemplateWrapper>
          <StatusTemplateBody
            title={t('Thank You!')}
            text={[
              t(
                'Profile.Your details have been successfully updated. We will only contact you if we require further details.'
              ),
            ]}
            icon='Success'
          />
          <StatusTemplateButtons>
            <Button.Primary size='lg' onClick={goToDashboard}>
              {t('Go To Dashboard')}
            </Button.Primary>
          </StatusTemplateButtons>
        </StatusTemplateWrapper>
      )}
    </Loading>
  )
}

export const shouldRedirectToSumsub = (account: AccountDetailedDto | undefined): boolean => {
  return !!(account && isSumsubSupported(account) && !isAccountActivatedStatus(account))
}
